<template>
  <div class="collection-page">
    <nav-header
      :title="$englishPreferred ? 'My counts' : 'Mine tællinger'"
      @synchronize-list="prepareList"
    ></nav-header>

    <main class="container collection-page-container">
      <loading :active="isLoading" :is-full-page="true"></loading>

      <header class="collection-page-intro">
        <span class="collection-page-intro-icon" aria-hidden="true">
          <i class="material-icons">history</i>
        </span>
        <div>
          <span class="collection-page-kicker">
            {{ $englishPreferred ? "Count archive" : "Tællearkiv" }}
          </span>
          <h1>
            {{
              $englishPreferred
                ? "Your registered counts"
                : "Dine registrerede tællinger"
            }}
          </h1>
          <p>
            {{
              $englishPreferred
                ? "Open a count to review or edit its observations."
                : "Åbn en tælling for at se eller rette dens observationer."
            }}
          </p>
        </div>
      </header>

      <section
        v-if="visibleCollections.length"
        class="collection-card-list"
        :aria-label="
          $englishPreferred ? 'Registered counts' : 'Registrerede tællinger'
        "
      >
        <article
          v-for="collection in visibleCollections"
          :key="collection.id"
          class="collection-card"
          :class="{ 'is-finn': collection.isFinnCount }"
        >
          <router-link
            class="collection-card-link"
            :to="{ name: 'SeeCount', params: { id: collection.id } }"
          >
            <div class="collection-card-heading">
              <span class="collection-card-icon" aria-hidden="true">
                <i class="material-icons">
                  {{ collection.isFinnCount ? "park" : "location_on" }}
                </i>
              </span>
              <div class="collection-card-title">
                <span class="collection-type-label">
                  {{
                    collection.isFinnCount
                      ? $englishPreferred
                        ? "FiNN count"
                        : "FiNN-tælling"
                      : $englishPreferred
                      ? "Spontaneous count"
                      : "Spontantælling"
                  }}
                </span>
                <h2 v-if="collection.site != null">
                  {{ collection.site.na }}
                </h2>
              </div>
              <span
                v-if="collection.countPoints[0] && seePoints"
                class="collection-points"
              >
                <strong>{{ collection.countPoints[0].points }}</strong>
                {{ $englishPreferred ? "points" : "point" }}
              </span>
            </div>

            <p
              v-if="collection.isFinnCount && collection.finnSiteId"
              class="collection-finn-site"
            >
              <i class="material-icons" aria-hidden="true">nature</i>
              FiNN · {{ getFinnSiteTitle(collection) }}
            </p>

            <div class="collection-card-meta">
              <span>
                <i class="material-icons" aria-hidden="true">grid_on</i>
                {{ $englishPreferred ? "Square" : "Kvadrat" }}
                {{ collection.atlasSquareNumber }}
              </span>
              <span>
                <i class="material-icons" aria-hidden="true">schedule</i>
                {{ collection.date }} · {{ collection.startTime }}–{{
                  collection.endTime
                }}
              </span>
            </div>

            <div class="collection-card-footer">
              <span
                class="collection-sync-state"
                :class="{
                  'needs-sync': !collection.apiId || collection.unsynched,
                }"
              >
                <i class="material-icons" aria-hidden="true">
                  {{
                    !collection.apiId || collection.unsynched
                      ? "cloud_upload"
                      : "cloud_done"
                  }}
                </i>
                {{
                  !collection.apiId || collection.unsynched
                    ? $englishPreferred
                      ? "Waiting to synchronize"
                      : "Afventer synkronisering"
                    : $englishPreferred
                    ? "Synchronized"
                    : "Synkroniseret"
                }}
              </span>
              <span class="collection-open-label">
                {{ $englishPreferred ? "Open" : "Åbn" }}
                <i class="material-icons" aria-hidden="true">chevron_right</i>
              </span>
            </div>
          </router-link>
        </article>
      </section>

      <div v-else-if="!isLoading" class="collection-empty-state">
        <i class="material-icons" aria-hidden="true">playlist_add</i>
        <h2>
          {{ $englishPreferred ? "No counts yet" : "Ingen tællinger endnu" }}
        </h2>
        <p>
          {{
            $englishPreferred
              ? "Your completed counts will appear here."
              : "Dine afsluttede tællinger vises her."
          }}
        </p>
      </div>

      <div v-if="visibleCollections.length" class="collection-load-more-wrap">
        <button @click="showNextPage()" class="collection-load-more-button">
          <i class="material-icons" aria-hidden="true">expand_more</i>
          {{ $englishPreferred ? "Show more counts" : "Vis flere tællinger" }}
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import collectionRepository from "../idb/repositories/collectionRepository";
import observationRepository from "../idb/repositories/observationRepository";
import countPointsRepository from "../idb/repositories/countPointsRepository";
import finnSiteRepository from "../idb/repositories/finnSiteRepository";
import NavHeader from "@/components/navbars/NavHeader.vue";
import axios from "axios";
// Import component
import Loading from "vue3-loading-overlay";
// Import stylesheet
import "vue3-loading-overlay/dist/vue3-loading-overlay.css";

let localCollections = [];

export default {
  components: {
    NavHeader,
    Loading,
  },
  data() {
    return {
      navTitle: "Mine tællinger",
      collections: [],
      isLoading: true,
      seePoints: "true",
      pageSize: 10,
      currentPageNumber: 1,
      finnSites: [],
    };
  },
  computed: {
    sortedCollections() {
      // Create a copy of the collections array
      const sortedArray = [...this.collections];

      // Sort the copied array based on date and startTime
      return sortedArray.sort((a, b) => {
        // Compare the dates
        const dateComparison = b.date.localeCompare(a.date);

        if (dateComparison === 0) {
          // If the dates are the same, compare the startTimes
          return b.startTime.localeCompare(a.startTime);
        } else {
          return dateComparison;
        }
      });
    },
    visibleCollections() {
      return this.sortedCollections.filter((collection) => !collection.deleted);
    },
  },
  methods: {
    getFinnSiteTitle(collection) {
      const site = this.finnSites.find(
        (finnSite) => finnSite.id === collection.finnSiteId
      );
      return site ? site.title : "";
    },
    async showNextPage() {
      this.isLoading = true;
      let finishedPreparing = false;
      let isOnline = window.navigator.onLine ? true : false;
      this.currentPageNumber++;
      if (isOnline) {
        // User is online
        // 1. Get collections from API
        await this.getAPICollections()
          .then(async (response) => {
            if (response.data.length > 0) {
              // 1.2. Insert each of the collections
              response.data.forEach((collection) => {
                delete collection.observations;

                let countPoint = collection.spontanPointForUser;
                delete collection.spontanPointForUser;

                collectionRepository
                  .insertCollection(collection)
                  .then((collectionId) => {
                    collection.id = collectionId;
                    collection.countPoints = [countPoint];
                    this.collections.push(collection);

                    if (countPoint != null) {
                      countPoint.datetime = new Date(countPoint.datetime);
                      this.addCountPointsToDatabase(countPoint, collectionId);
                    }
                  });
              });
            }
          })
          .catch(() => {
            console.log("Could not receive API collections");
          });
      }
      finishedPreparing = true;
      if (finishedPreparing) {
        this.isLoading = false;
      }
    },
    async prepareList() {
      let finishedPreparing = false;
      // Check if user is online
      let isOnline = window.navigator.onLine ? true : false;

      if (isOnline) {
        // User is online
        // 1. Get collections from API, and if we get response, first remove all local synchronized collections + their points and observations
        await this.getAPICollections()
          .then(async (response) => {
            if (response.data.length > 0) {
              // 1.1. Remove clear all collections and points that are synchronized
              await collectionRepository.deleteSynchedCollections();

              // 1.2. Insert each of the collections
              response.data.forEach((collection) => {
                delete collection.observations;

                let countPoint = collection.spontanPointForUser;
                delete collection.spontanPointForUser;

                collectionRepository
                  .insertCollection(collection)
                  .then((collectionId) => {
                    if (countPoint != null) {
                      countPoint.datetime = new Date(countPoint.datetime);
                      this.addCountPointsToDatabase(countPoint, collectionId);
                    }
                  });
              });
            }
          })
          .catch(() => {
            console.log("Could not receive API collections");
          });
      }
      // 2 get local collections
      if (await this.getLocalCollections()) {
        // 2.1 prepare points for local collection
        for (let collection of this.localCollections) {
          let countPoints =
            await countPointsRepository.getCountPointsByCollectionId(
              collection.id
            );
          if (countPoints != null) {
            collection.countPoints = countPoints;
          }
        }
        this.collections = this.localCollections;
      }
      // 3 now sync unysnched and deleted collection to API
      await this.synchronizeList();

      // 4 finished sync - remove loading spinner
      finishedPreparing = true;
      if (finishedPreparing) {
        this.isLoading = false;
      }
    },

    addCountPointsToDatabase(countPoint, collectionId) {
      countPointsRepository.deleteCountPointsByCollectionId(collectionId);
      countPoint.collectionId = collectionId;
      delete countPoint.id;
      countPointsRepository.insertCountPoints(countPoint);
    },

    async getLocalCollections() {
      this.localCollections = await collectionRepository.getCollections();
      return true;
    },
    getAPICollections() {
      return axios.get("/spontan/collections/mine/paginated", {
        params: {
          page: this.currentPageNumber,
          limit: this.pageSize,
        },
      });
    },
    async synchronizeList() {
      console.log("synkroniserer nye ture fra listen med API");
      for (let collection of this.collections) {
        // Prepare collection and observations for interaction with API
        let observations =
          await observationRepository.getObservationsByCollectionId(
            collection.id
          );
        let unwrappedCollection = {
          ...JSON.parse(JSON.stringify(collection)),
        };
        if (collection.apiId == null && collection.deleted != true) {
          console.log("inserting to API");
          //Insert collection in API
          axios
            .post("/spontan/collections/", unwrappedCollection)
            .then((response) => {
              let collectionApiId = response.data;
              console.log(
                "adding observations to DB, collectionApiID: " + collectionApiId
              );
              unwrappedCollection.apiId = collectionApiId;
              collection.apiId = collectionApiId;

              collectionRepository.insertCollection(unwrappedCollection);
              observations.forEach((observation) => {
                observation.collectionApiId = collectionApiId;
                // For each observation send it to API, and then set the apiId for it, and update it in local database
                axios
                  .post("/spontan/observations/", observation)
                  .then((response) => {
                    let observationApiId = response.data;
                    observation.apiId = observationApiId;
                    observation.unsynched = false;
                    observationRepository.insertObservation(observation);
                  });
              });
            });
        }
      }
      // Finished interacting with API
      return true;
    },
  },
  beforeMount() {
    // get all finn sites from indexedDB and assign to finnSites
    finnSiteRepository.getFinnSites().then((response) => {
      this.finnSites = response;
    });
    // prepare list of collections
    this.prepareList();
  },
  mounted() {
    if ($cookies.isKey("seePoints")) {
      this.seePoints = $cookies.get("seePoints");
    }
  },
};
</script>
